<template>
  <div>
    <div v-if="showPassword">
      <Password @save="load" :tries="passwordTries" :showBack="true" />
    </div>
    <div
      v-else
      class="pinwall"
      :style="`min-height: ${windowHeight}px`"
      @dragover.stop.prevent="drag($event, true)"
      @dragleave.stop.prevent="drag($event, false)"
    >
      <FileInput
        :id="postId"
        v-if="showFileUpload && !showAdd && user"
        ref="fileInput"
        :fullScreen="true"
        @fileUploaded="uploaded"
      />

      <FixedHeader
        @clickedLogin="(showLogin = !showLogin), (showAdd = false)"
        :showLogin="showLogin"
        :showForm="showAdd || showEdit"
        :showLoginBtn="!showSearch || windowWidth >= 700"
        @show="showSearchInput"
        @input="search"
        :showSearchInput="showSearch"
        :view="'pinwall'"
      >
        <b-button
          v-if="!showSearch || windowWidth >= 700"
          class="back-button"
          type="is-text"
          icon-left="arrow-left"
          size="is-medium"
          @click="goTo()"
        ></b-button>
        <b-dropdown
          class="menu-dropdown"
          aria-role="list"
          v-if="!showSearch || windowWidth >= 700"
        >
          <button class="button is-text" slot="trigger">
            <b-icon icon="dots-horizontal"></b-icon>
          </button>
          <b-dropdown-item
            v-if="pinwall.editable && user"
            @click="showEdit = !showEdit"
            aria-role="listitem"
          >
            <b-icon icon="pencil" size="is-small"></b-icon>{{ $t("edit") }}
          </b-dropdown-item>
          <b-dropdown-item
            v-if="pinwall.options && pinwall.options.comments ? true : false"
            @click="openComments(null)"
          >
            <b-icon icon="comment" size="is-small"></b-icon
            >{{ `${pinwall.comments ? pinwall.comments.length : 0} Comments` }}
          </b-dropdown-item>
          <b-dropdown-item @click="openScreen('grid')">
            <b-icon icon="television" size="is-small"></b-icon>Social Screen
          </b-dropdown-item>
          <b-dropdown-item @click="openScreen('loop')">
            <b-icon icon="refresh" size="is-small"></b-icon>Loop
          </b-dropdown-item>
          <b-dropdown-item
            v-if="pinwall.editable"
            @click="deletePinwallItem"
            aria-role="listitem"
          >
            <b-icon icon="delete" size="is-small"></b-icon>{{ $t("delete") }}
          </b-dropdown-item>
        </b-dropdown>
        <Edit
          @updated="updateRoute"
          @close="showEdit = false"
          :pinwall="pinwall"
          v-if="showEdit"
        ></Edit>
        <div
          v-if="
            (windowWidth > mobileWidth && !showSearch) || windowWidth >= 700
          "
          :style="`width: ${Math.round(windowWidth / 6)}px`"
          class="name"
        >
          <p>{{ pinwall.name }}</p>
        </div>
        <b-button
          v-if="!showSearch || windowWidth >= 700"
          :disabled="disablePostAddButton || showPassword"
          class="add-button action-button"
          :title="postButtonTitle"
          :style="user ? 'right: 67px' : ''"
          :type="showAdd ? 'is-danger' : 'is-success'"
          @click="(showAdd = !showAdd), (showLogin = false)"
          rounded
          :icon-left="showAdd ? 'close' : 'plus'"
          >{{ showAdd ? "Close" : "Post" }}</b-button
        >
      </FixedHeader>
      <Add
        @added="showAdd = false"
        :tenantId="tenantId"
        :pinwallId="pinwall.id"
        :password="pinwall.password || password"
        :view="this.view.list ? 'list' : ''"
        :sort="this.sort"
        v-if="showAdd"
      ></Add>
      <VideoChat
        v-if="
          pinwall &&
            pinwall.options &&
            pinwall.options.videoChat &&
            (chatOptions || (user && user.username)) &&
            hasLoaded
        "
        :subscribeId="
          pinwall.options.private
            ? `${pinwall.id}-${pinwall.password}-videoChat`
            : `${pinwall.id}-videoChat`
        "
        :chatOptions="chatOptions"
      />
      <div class="buttons">
        <div>
          <b-button
            class="copyLink is-text"
            :style="windowWidth <= mobileWidth && view.list ? 'right: 6px' : ''"
            icon-left="link"
            :title="$t('buttons.copyLink')"
            @click="copyLink()"
          ></b-button>
          <b-button
            class="download is-warning"
            :style="windowWidth <= mobileWidth && view.list ? 'right: 6px' : ''"
            icon-left="download"
            :title="$t('download')"
            @click="isDownloadModalActive = !isDownloadModalActive"
          ></b-button>
          <b-button
            class="creative"
            v-if="windowWidth > mobileWidth && view.grid"
            @click="changeEditMode(isCreativeMode)"
            icon-left="move-resize-variant"
            :title="
              isCreativeMode
                ? $t('views.enableResize')
                : $t('views.disableResize')
            "
            :class="isCreativeMode ? 'is-creative' : 'is-text'"
          ></b-button>
          <b-button
            v-if="windowWidth > mobileWidth"
            icon-left="view-quilt"
            :title="$t('views.gridView')"
            :class="view.grid ? 'is-selected' : 'is-text'"
            @click="setGridView"
          ></b-button>
          <b-button
            v-if="windowWidth > mobileWidth"
            icon-left="view-agenda"
            :title="$t('views.listView')"
            :class="view.list ? 'is-selected' : 'is-text'"
            @click="setListView"
          ></b-button>
          <b-dropdown
            aria-role="list"
            v-model="sort"
            @change="sortPosts"
            position="is-bottom-left"
          >
            <button
              class="button is-primary is-rounded sort-button"
              slot="trigger"
            >
              <span>{{
                sort !== "" ? $t(`sort.${sort}`) : $t("sort.creative")
              }}</span>
              <b-icon icon="menu-down"></b-icon>
            </button>
            <b-dropdown-item disabled>{{ $t("sort.sorting") }}</b-dropdown-item>
            <b-dropdown-item value aria-role="listitem">{{
              $t("sort.creative")
            }}</b-dropdown-item>
            <b-dropdown-item value="new" aria-role="listitem">{{
              $t("sort.new")
            }}</b-dropdown-item>
            <b-dropdown-item value="old" aria-role="listitem">{{
              $t("sort.old")
            }}</b-dropdown-item>
            <b-dropdown-item value="likes" aria-role="listitem">{{
              $t("sort.likes")
            }}</b-dropdown-item>
          </b-dropdown>
        </div>
      </div>
      <Grid
        v-if="view.grid && windowWidth > mobileWidth"
        :posts="pinwall.posts"
        :editable="isCreativeMode"
        :withComments="
          pinwall.options && pinwall.options.comments ? true : false
        "
        @layoutUpdated="save"
        @deletePost="deletePostItem"
        @approvePost="approvePostItem"
        @editPost="editPostItem"
        @openComments="openComments"
        @fullscreen="fullscreen"
        @filePreview="filePreview"
        @ended="$refs.controls.next()"
      ></Grid>
      <List
        v-if="view.list || windowWidth <= mobileWidth"
        :posts="pinwall.posts"
        :withComments="
          pinwall.options && pinwall.options.comments ? true : false
        "
        @deletePost="deletePostItem"
        @approvePost="approvePostItem"
        @editPost="editPostItem"
        @openComments="openComments"
        @fullscreen="fullscreen"
        @filePreview="filePreview"
        @ended="$refs.controls.next()"
      ></List>

      <button
        v-if="nextPinwall && showNextPinwall"
        @click="openNextPinwall()"
        class="button is-rounded is-warning next-pinwall-button"
      >
        <span>{{ nextPinwall.name }}</span>
        <b-icon icon="arrow-right"></b-icon>
      </button>

      <FileInput
        :id="postId"
        v-if="!showAdd && user && renderFileInput"
        ref="fileInput"
        @fileUploaded="uploaded"
        :showButton="true"
        :fullScreen="true"
      />

      <b-modal :active.sync="isDownloadModalActive">
        <Download
          :tenantId="tenantId"
          :space="space"
          :pinwallId="pinwallId"
          :password="password"
        />
      </b-modal>

      <b-modal :active.sync="isEditModalActive">
        <PostEdit :post="post" @save="savePost" @cancel="cancelEdit" />
      </b-modal>

      <b-modal class="fullscreen" :active.sync="isFullscreenModalActive">
        <Post :post="post" :mode="'fullscreen'" />
      </b-modal>

      <b-modal class="fullscreen" :active.sync="isFilePreviewModalActive">
        <FileViewer :post="post" :mode="'fullscreen'" />
      </b-modal>

      <b-modal :active.sync="isCommentModalActive">
        <Comment :postId="post ? post.id : ''" :pinwallId="pinwall.id" />
      </b-modal>
      <Controls ref="controls" :posts="pinwall.posts" />
    </div>
    <Chat
      v-if="
        pinwall &&
          pinwall.options &&
          pinwall.options.chat &&
          (chatOptions || (user && user.username)) &&
          hasLoaded
      "
      :pinwallId="pinwall.id"
      :subscribeId="
        pinwall.options.private
          ? `${pinwall.id}-${pinwall.password}-chat`
          : `${pinwall.id}-chat`
      "
      :chatOptions="chatOptions"
    />

    <div v-if="isLoading" class="loading-container">
      <b-loading
        v-if="view.grid"
        :is-full-page="false"
        :active.sync="isLoading"
        :can-cancel="true"
      ></b-loading>
    </div>
  </div>
</template>

<script>
import FixedHeader from "@/components/FixedHeader";
import Add from "@/components/pinwall/Add";
import Edit from "@/components/pinwall/Edit";
import Grid from "@/components/pinwall/Grid";
import List from "@/components/pinwall/List";
import Chat from "@/components/pinwall/Chat";
import FileInput from "@/components/pinwall/FileInput";
import FileViewer from "@/components/pinwall/FileViewer";
import Controls from "@/components/core/Controls";
import Password from "@/components/core/Password";
import Download from "@/components/pinwall/Download";
import PostEdit from "../components/pinwall/PostEdit";
import Post from "../components/pinwall/Post";
import Comment from "../components/pinwall/Comment";
import VideoChat from "../components/pinwall/VideoChat";
import {
  generateId,
  checkIfMobileOrTablet,
  copyStringToClipboard
} from "../utils/helpers";
import { mapActions, mapGetters, mapMutations } from "vuex";
import { deleteVideo } from "../services/media";
import { goTo } from "../mixins/goTo.js";
import { getScrollY } from "../mixins/getScrollY.js";
import { openUsernameDialog } from "../mixins/openUsernameDialog.js";

export default {
  name: "pinwall",
  components: {
    FixedHeader,
    Add,
    Grid,
    List,
    Edit,
    Chat,
    FileInput,
    FileViewer,
    Password,
    PostEdit,
    Post,
    Comment,
    Controls,
    VideoChat,
    Download
  },
  data: () => {
    return {
      sort: "",
      showAdd: false,
      isCreativeMode: false,
      isFullScreen: false,
      isDownloading: false,
      showLogin: false,
      showEdit: false,
      showSearch: false,
      showPassword: false,
      renderFileInput: true,
      isMobileOrTablet: false,
      hasScrollbar: false,
      showNextPinwall: false,
      socketEmit: false,
      subscribeId: "",
      initialLoad: true,
      draggingOver: false,
      showFileUpload: false,
      isEditModalActive: false,
      isSocketSubscribed: false,
      isDataLoaded: false,
      nextPinwall: null,
      postButtonTitle: "Add post",
      disablePostAddButton: false,
      lastSorting: "",
      isFullscreenModalActive: false,
      isFilePreviewModalActive: false,
      isDownloadModalActive: false,
      isCommentModalActive: false,
      mobileWidth: process.env.VUE_APP_MOBILE_WIDTH,
      loadCommentsInterval: null,
      isLoading: false,
      isFullPage: true,
      hasLoaded: false,
      password: "",
      doit: null,
      size: null,
      dragLeaveCount: 0,
      postId: generateId(),
      post: null,
      postBeforeEdit: {
        text: "",
        style: "",
        links: null
      },
      passwordTries: 0,
      view: {
        grid: false,
        list: false
      }
    };
  },
  mixins: [goTo, getScrollY, openUsernameDialog],
  computed: {
    ...mapGetters([
      "pinwall",
      "user",
      "comments",
      "selectedSpace",
      "storedPinwalls",
      "viewHeight",
      "chatOptions",
      "pinwalls"
    ])
  },
  watch: {
    user: "load",
    windowWidth: "changePostPosition",
    comments: "setCommentsToPinwall",
    getScrollY: "handleScroll"
  },
  methods: {
    ...mapActions([
      "loadPinwall",
      "loadPinwalls",
      "loadComments",
      "updatePost",
      "saveLayout",
      "deletePost",
      "deletePinwall",
      "createPost"
    ]),
    ...mapMutations([
      "setCommentsToPinwall",
      "setPinwall",
      "setStoredPinwall",
      "setViewHeight",
      "setPosts",
      "setChatOptions",
      "setPlayMedia"
    ]),
    openScreen(mode) {
      let url = `/${this.tenantId}/${this.pinwall.space}/${
        this.pinwall.pinwallId
      }/screen?${
        this.pinwall.options.private ? "password=" + this.pinwall.password : ""
      }&mode=${mode}`;
      this.$router.push(url);
    },
    openNextPinwall() {
      if (this.nextPinwall) {
        let url =
          this.tenantId === "pinwall"
            ? `/${this.nextPinwall.space}/${this.nextPinwall.pinwallId}`
            : `/${this.tenantId}/${this.nextPinwall.space}/${this.nextPinwall.pinwallId}`;
        this.$router.push(url);
      }
    },
    save() {
      if (
        this.sort === "" &&
        this.isCreativeMode &&
        !this.isLoading &&
        this.user &&
        (this.pinwall.options.collaboration || this.pinwall.editable)
      ) {
        this.saveLayout({
          pinwallId: this.pinwall.id,
          posts: this.pinwall.posts,
          tenant: this.tenantId
        }).then(data => {
          var emitModel = {
            postList: data,
            view: this.view.list ? "list" : "",
            windowWidth: this.windowWidth,
            sort: this.sort
          };

          this.$socket.emit(
            process.env.VUE_APP_SOCKET_ON,
            this.subscribeId,
            emitModel
          );
        });
      }
    },
    copyLink() {
      const link = this.pinwall.options.private
        ? `${location.origin}${location.pathname}?password=${this.pinwall.password}&sort=${this.sort}`
        : location.href;

      copyStringToClipboard(link);

      this.$buefy.toast.open({
        message: unescape(this.$t("linkCopied")),
        type: "is-success",
        duration: 3000,
        position: "is-top-right"
      });
    },
    setListView() {
      this.setPlayMedia(false);
      this.initialLoad = true;
      this.view.list = true;
      this.view.grid = false;
      this.setViewHeight(0);
      this.load();
    },
    setGridView() {
      this.setPlayMedia(false);
      this.initialLoad = true;
      this.view.grid = true;
      this.view.list = false;
      this.setViewHeight(0);
      this.load();
    },
    uploaded(files) {
      this.image = files.image;
      this.video = files.video;
      this.audio = files.audio;
      this.proportion = files.proportion;
      this.file = files.file;
      this.pendingFiles = files.pendingFiles;
      this.addFromSearch = false;
      this.multiUpload = true;
      this.create();
    },
    drag(e, visible) {
      if (this.showAdd) {
        this.showFileUpload = false;
      } else if (this.showFileUpload) {
        this.dragLeaveCount += !visible ? this.dragLeaveCount + 1 : 0;
        this.showFileUpload = this.dragLeaveCount > 1 ? false : true;
      } else {
        this.showFileUpload =
          e.dataTransfer.types.length === 1 &&
          e.dataTransfer.types[0] === "Files";
        this.dragLeaveCount = 0;
      }
    },
    create() {
      this.createPost({
        id: this.postId,
        pinwallId: this.pinwall.id,
        name: this.name,
        image: this.image,
        video: this.video,
        audio: this.audio,
        file: this.file,
        proportion: this.proportion,
        view: this.view.list ? "list" : ""
      }).then(data => {
        var emitModel = {
          postList: data,
          view: this.view.list ? "list" : "",
          windowWidth: this.windowWidth,
          sort: this.sort
        };
        this.$socket.emit(
          process.env.VUE_APP_SOCKET_ON,
          this.subscribeId,
          emitModel
        );
        this.postId = generateId();
        if (this.pendingFiles > 0) {
          this.$refs.fileInput.uploadNextFile();
        } else {
          this.showFileUpload = false;
          this.renderFileInput = false;
          this.$nextTick(() => {
            this.renderFileInput = true;
          });
        }
      });
    },
    search(value) {
      const searchUrl = `/search?searchValue=${value}&backUrl=/${this.tenantId}/${this.space}/${this.pinwallId}`;
      this.goTo(searchUrl);
    },
    showSearchInput(visible) {
      this.showSearch = visible;
    },
    deletePinwallItem() {
      this.$buefy.dialog.confirm({
        message: this.$t("pinwall.delete"),
        onConfirm: () => {
          this.deletePinwall(this.pinwall).then(data => {
            const subscribeId = this.selectedSpace.password
              ? `${this.tenantId}-${this.selectedSpace.spaceId}-${this.selectedSpace.password}`
              : `${this.tenantId}-${this.selectedSpace.spaceId}`;

            const emitModel = {
              type: "delete",
              pinwall: data
            };

            this.$socket.emit(
              process.env.VUE_APP_SOCKET_ON,
              subscribeId,
              emitModel
            );
            this.$router.back();
          });
        }
      });
    },
    editPostItem(post) {
      this.post = post;
      this.postBeforeEdit = {
        text: this.post.text,
        style: this.post.style,
        links: this.post.links
      };
      this.isEditModalActive = true;
    },
    approvePostItem(post) {
      post.approved = true;
      this.savePost(post);
    },
    fullscreen(post) {
      this.post = post;
      this.isFullscreenModalActive = true;
    },
    filePreview(post) {
      this.post = post;
      this.isFilePreviewModalActive = true;
    },
    openComments(post) {
      this.post = post;
      this.isCommentModalActive = true;
    },
    savePost(post) {
      post.view = this.view.list ? "list" : "";
      this.updatePost(post).then(data => {
        var emitModel = {
          postList: data,
          view: this.view.list ? "list" : "",
          windowWidth: this.windowWidth,
          sort: this.sort
        };
        this.$socket.emit(
          process.env.VUE_APP_SOCKET_ON,
          this.subscribeId,
          emitModel
        );
      });
      this.isEditModalActive = false;
    },
    cancelEdit() {
      this.post.text = this.postBeforeEdit.text;
      this.post.style = this.postBeforeEdit.style;
      this.post.links = this.postBeforeEdit.links;
      this.isEditModalActive = false;
    },
    deletePostItem(post) {
      this.initialLoad = true;
      post.view = this.view.list ? "list" : "";

      this.deletePost(post).then(data => {
        if (post.video && post.video.type === "local") {
          deleteVideo(post.video.fileName);
        }

        var emitModel = {
          postList: data,
          view: this.view.list ? "list" : "",
          windowWidth: this.windowWidth,
          sort: this.sort
        };
        this.$socket.emit(
          process.env.VUE_APP_SOCKET_ON,
          this.subscribeId,
          emitModel
        );
      });
    },
    sortPosts(value) {
      this.initialLoad = true;
      this.lastSorting = value;
      this.isCreativeMode = value === "" ? true : false;
      this.sort = value;

      this.$router.push({
        query: { sort: this.sort ? this.sort : "creative" }
      });

      // this.load();
    },
    changeEditMode(isCreativeMode) {
      this.isCreativeMode = !isCreativeMode;
      this.sort = this.isCreativeMode ? "" : this.lastSorting;
      this.initialLoad = true;
      this.load();
    },
    setNextPinwall(lastIndex) {
      if (this.pinwalls.length === 1 || this.nextPinwall) {
        return;
      }

      const index =
        lastIndex || this.pinwalls.findIndex(o => o.id === this.pinwall.id);
      if (index < this.pinwalls.length - 1) {
        let nextPinwall = this.pinwalls[index + 1];
        if (!nextPinwall.private) {
          this.nextPinwall = nextPinwall;
        } else {
          this.setNextPinwall(index);
        }
      } else {
        this.nextPinwall = this.pinwalls[0];
      }
    },
    changePostPosition() {
      if (
        window.fullScreen ||
        (window.innerWidth == screen.width &&
          window.innerHeight == screen.height)
      ) {
        this.isFullScreen = true;
        return;
      } else if (this.isFullScreen) {
        this.isFullScreen = false;
      }

      const that = this;
      clearTimeout(this.doit);
      this.doit = setTimeout(function() {
        that.load();
      }, 500);
    },
    updateRoute() {
      if (this.pinwall.pinwallId != this.pinwallId) {
        this.$router.push(`${this.pinwall.pinwallId}`);
      }
      this.showEdit = false;
    },
    setShowNextPinwall() {
      const scrollYPosition = window.scrollY + window.innerHeight;

      if (this.showNextPinwall) {
        if (this.view.list || this.windowWidth <= this.mobileWidth) {
          this.showNextPinwall =
            document.documentElement.scrollHeight - scrollYPosition < 200;
        } else {
          this.showNextPinwall =
            document.documentElement.scrollHeight - scrollYPosition < 400;
        }
      }

      if (this.pinwall.posts.length < this.pinwall.postsCount) {
        this.showNextPinwall = false;
      } else if (this.view.list || this.windowWidth <= this.mobileWidth) {
        this.showNextPinwall =
          document.documentElement.scrollHeight - scrollYPosition < 100;
      } else {
        this.showNextPinwall =
          document.documentElement.scrollHeight - scrollYPosition < 300;
      }
    },
    loadCommentItems(id) {
      this.loadComments(id).then(comments => {
        this.setCommentsToPinwall(comments);
      });

      if (!this.loadCommentsInterval) {
        this.loadCommentsInterval = setInterval(() => {
          this.loadComments(id).then(comments => {
            this.setCommentsToPinwall(comments);
          });
        }, 30000);
      }
    },
    load(password, socketEmit, mounted) {
      const params = this.$route.params;
      this.tenantId = params.tenant || "pinwall";
      this.space = params.space || null;
      this.pinwallId = params.pinwallId || null;
      this.password =
        password && typeof password === "string" ? password : this.password;
      this.isLoading = true;
      this.loadPinwall({
        tenant: this.tenantId,
        space: this.space,
        pinwallId: this.pinwallId,
        password: this.password,
        sort: this.sort,
        socketEmit: socketEmit,
        view:
          this.view.list || this.windowWidth <= this.mobileWidth ? "list" : "",
        initialLoad: this.initialLoad,
        mounted: mounted
      })
        .then(pinwall => {
          this.isLoading = false;
          this.showPassword = false;
          this.showNextPinwall = false;
          this.isDataLoaded = true;
          this.initialLoad = false;
          this.hasLoaded = true;
          this.subscribeId =
            this.pinwall.id + (this.pinwall.password || this.password || "");

          if (
            pinwall.options.collaboration == null ||
            pinwall.options.collaboration
          ) {
            this.disablePostAddButton = !this.user;
            this.postButtonTitle = this.disablePostAddButton
              ? this.$t("buttons.addPostDisabled")
              : this.$t("buttons.addPost");
          } else {
            this.disablePostAddButton = !pinwall.editable;
            this.postButtonTitle = this.disablePostAddButton
              ? this.$t("buttons.addPostCollabDisabled")
              : this.$t("buttons.addPost");
          }

          if (!this.isSocketSubscribed) {
            this.sockets.subscribe(this.subscribeId, data => {
              let view = this.view.list ? "list" : "";
              if (
                data.sort === this.sort &&
                data.view === view &&
                data.windowWidth >= this.windowWidth - 150 &&
                data.windowWidth <= this.windowWidth + 150 &&
                !this.pinwall.options.approval
              ) {
                this.setPosts(data.postList);
              } else {
                this.load(null, true);
              }
            });
            this.isSocketSubscribed = true;
          }

          if (
            !this.user &&
            pinwall.options &&
            (pinwall.options.videoChat || pinwall.options.chat) &&
            !this.dialog &&
            !this.chatOptions
          ) {
            this.dialog = this.$buefy.dialog.prompt({
              title: "Chat",
              message: `Enter your name`,
              inputAttrs: {
                placeholder: "e.g. Pascal",
                maxlength: 20
              },
              trapFocus: true,
              confirmText: "Ok",
              // eslint-disable-next-line no-unused-vars
              onCancel: () => this.goTo(),
              onConfirm: value =>
                this.setChatOptions({
                  name: value
                })
            });
          }
          this.loadCommentItems(pinwall.id);
          this.setStoredPinwall({
            tenant: this.tenantId,
            space: this.space,
            pinwallId: this.pinwallId,
            password: this.pinwall.password,
            sort: this.sort,
            view: this.view,
            userId: this.user ? this.user.id : ""
          });

          const tenantId =
            this.pinwalls && this.pinwalls.length > 0
              ? this.pinwalls[0].tenant
              : "";

          if (tenantId != this.tenantId) {
            this.loadPinwalls({
              tenant: this.tenantId,
              space: "all",
              sort: "new"
            }).then(() => {
              this.setNextPinwall();
            });
          } else {
            this.setNextPinwall();
          }
          this.$nextTick(() => {
            this.setShowNextPinwall();
          });
        })
        .catch(() => {
          this.showPassword = true;
          this.passwordTries = this.passwordTries + 1;
          this.isLoading = false;
        });
    },
    handleScroll() {
      this.setShowNextPinwall();
      const scrollYPosition = window.scrollY + window.innerHeight;

      var additionalHeight = (this.viewHeight / 100) * 20;
      var wantedHeight =
        additionalHeight <= 540
          ? this.viewHeight + additionalHeight
          : this.viewHeight + 540;

      if (
        scrollYPosition >= wantedHeight &&
        this.pinwall.posts.length < this.pinwall.postsCount &&
        !this.isLoading
      ) {
        this.setViewHeight(scrollYPosition);
        this.load();
      }
    }
  },
  created() {
    if (this.user && !this.user.username) {
      this.openUsernameDialog(this.$t("user.enterUserName"));
    }

    window.scrollTo(0, 0);
    this.setViewHeight(0);
    const params = this.$route.params;
    this.tenantId = params.tenant || "pinwall";
    this.space = params.space || null;
    this.pinwallId = params.pinwallId || null;
    this.userId = this.user ? this.user.id : "";
    this.isMobileOrTablet = checkIfMobileOrTablet(navigator.userAgent);
    this.storedPinwall = null;
    let fallbackSort = "new";

    if (
      this.tenantId === "srf" ||
      this.tenantId === "rts" ||
      this.tenantId === "rts"
    ) {
      fallbackSort = "old";
    }

    if (this.storedPinwalls) {
      this.storedPinwall = this.storedPinwalls.find(item => {
        return (
          item.tenant === this.tenantId &&
          item.space === this.space &&
          item.pinwallId === this.pinwallId &&
          item.userId === this.userId
        );
      });
    }

    this.view.list =
      this.storedPinwall && this.storedPinwall.view
        ? this.storedPinwall.view.list
        : this.windowWidth <= this.mobileWidth;
    this.view.grid =
      this.storedPinwall && this.storedPinwall.view
        ? this.storedPinwall.view.grid
        : this.windowWidth > this.mobileWidth;
    this.sort =
      this.storedPinwall && this.storedPinwall.sort
        ? this.storedPinwall.sort
        : fallbackSort;

    this.sort = this.$route.query.sort || this.sort || fallbackSort;

    if (this.$route.query.sort && this.$route.query.sort === "creative") {
      this.sort = "";
    }

    if (!this.view.list && !this.view.grid) {
      this.view.list = this.windowWidth <= this.mobileWidth;
      this.view.grid = this.windowWidth > this.mobileWidth;
    }

    if (this.isMobileOrTablet) {
      this.view.list = true;
      this.view.grid = false;
    }

    this.password =
      this.storedPinwall && this.storedPinwall.password
        ? this.storedPinwall.password
        : this.$route.query.password;

    this.load(null, null, true);

    this.lastSorting = this.sort;
    this.isCreativeMode = this.sort === "" ? true : false;
  },
  beforeDestroy() {
    this.sockets.unsubscribe(this.subscribeId);
    clearTimeout(this.loadCommentsInterval);
    clearTimeout(this.doit);
    if (this.dialog) {
      this.dialog.close();
    }
    this.setPinwall({
      name: "",
      posts: []
    });
  }
};
</script>
